/** A strip of spotlight cards that pages itself; every card repeats a figure shown in full elsewhere on the page, so nothing lives only in a slide. */
import { ChevronLeft, ChevronRight, Pause, Play } from 'lucide-react'
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import {
  clampIndex,
  nextIndex,
  packCapacity,
  packSlides,
  prevIndex,
  shouldAutoAdvance,
  SLIDE_MS,
  trackHeight,
  type SlideWeight,
} from '../lib/autoSlide'

export interface Slide {
  key: string
  node: ReactNode
  /** Column units the card needs; the strip packs cards into pages by these. */
  weight: SlideWeight
  /** A card never shares a page with a card of another group. */
  group?: string
}

function useReducedMotion(): boolean {
  const query = '(prefers-reduced-motion: reduce)'
  const [reduced, setReduced] = useState(
    () => typeof window !== 'undefined' && window.matchMedia?.(query).matches === true,
  )
  useEffect(() => {
    const mql = window.matchMedia?.(query)
    if (!mql) return
    const onChange = () => setReduced(mql.matches)
    mql.addEventListener('change', onChange)
    return () => mql.removeEventListener('change', onChange)
  }, [])
  return reduced
}

function usePageHidden(): boolean {
  const [hidden, setHidden] = useState(() => typeof document !== 'undefined' && document.hidden)
  useEffect(() => {
    const onChange = () => setHidden(document.hidden)
    document.addEventListener('visibilitychange', onChange)
    return () => document.removeEventListener('visibilitychange', onChange)
  }, [])
  return hidden
}

function useWidth<T extends HTMLElement>(): [React.RefObject<T | null>, number] {
  const ref = useRef<T>(null)
  const [width, setWidth] = useState(0)
  // Measured before paint so a packed strip never shows one layout and then re-packs into another.
  useLayoutEffect(() => {
    if (ref.current) setWidth(ref.current.getBoundingClientRect().width)
  }, [])
  useEffect(() => {
    const el = ref.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(([entry]) => setWidth(entry?.contentRect.width ?? 0))
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  return [ref, width]
}

const controlClass =
  'inline-flex items-center justify-center rounded-md p-1 text-slate-600 hover:bg-slate-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus pointer-coarse:min-h-11 pointer-coarse:min-w-11 dark:text-slate-300 dark:hover:bg-slate-700'

export function AutoSlide({
  label,
  slides,
  intervalMs = SLIDE_MS,
}: {
  label?: string
  slides: Slide[]
  intervalMs?: number
}) {
  const { t } = useTranslation()
  const [viewportRef, width] = useWidth<HTMLDivElement>()
  const groups = packSlides(
    slides.map((s) => s.weight),
    packCapacity(width),
    slides.map((s) => s.group),
  )
  const pages = groups.length

  const [index, setIndex] = useState(0)
  const [paused, setPaused] = useState(false)
  const [hovered, setHovered] = useState(false)
  const [focused, setFocused] = useState(false)
  const reducedMotion = useReducedMotion()
  const hidden = usePageHidden()
  const swipeFrom = useRef<number | null>(null)
  const page = clampIndex(index, pages)
  const auto = shouldAutoAdvance({ reducedMotion, paused, hovered, focused, hidden, pages })
  const pageRefs = useRef<(HTMLDivElement | null)[]>([])
  const [heights, setHeights] = useState<number[]>([])
  const layoutKey = groups.map((g) => g.join(',')).join('|')

  // The strip takes the shown page's height, so a short page leaves no band reserved for the tallest one.
  useLayoutEffect(() => {
    const els = pageRefs.current.slice(0, pages)
    const read = () => setHeights(els.map((el) => el?.getBoundingClientRect().height ?? 0))
    read()
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(read)
    for (const el of els) if (el) ro.observe(el)
    return () => ro.disconnect()
  }, [pages, layoutKey])

  // One timeout per shown page, keyed on the page, so a manual move restarts the clock.
  useEffect(() => {
    if (!auto) return
    const id = window.setTimeout(() => setIndex(nextIndex(page, pages)), intervalMs)
    return () => window.clearTimeout(id)
  }, [auto, page, pages, intervalMs])

  if (pages === 0) return null

  return (
    <section
      aria-roledescription="carousel"
      aria-label={label ?? t('homewall.spotlight')}
      className="min-w-0"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFocused(false)
      }}
      onKeyDown={(e) => {
        if (pages < 2 || e.defaultPrevented) return
        if (e.key === 'ArrowRight') setIndex(nextIndex(page, pages))
        else if (e.key === 'ArrowLeft') setIndex(prevIndex(page, pages))
      }}
    >
      <div
        ref={viewportRef}
        className="-mx-1.5 touch-pan-y overflow-hidden py-0.5"
        aria-live={auto ? 'off' : 'polite'}
        onPointerDown={(e) => {
          if (e.pointerType !== 'mouse') swipeFrom.current = e.clientX
        }}
        onPointerUp={(e) => {
          const from = swipeFrom.current
          swipeFrom.current = null
          if (from === null || pages < 2) return
          const dx = e.clientX - from
          if (Math.abs(dx) < 40) return
          setIndex(dx < 0 ? nextIndex(page, pages) : prevIndex(page, pages))
        }}
        onPointerCancel={() => {
          swipeFrom.current = null
        }}
      >
        <div
          className="flex items-start motion-safe:transition-[transform,height] motion-safe:duration-500 motion-safe:ease-out"
          style={{ transform: `translateX(-${page * 100}%)`, height: trackHeight(heights, page) }}
        >
          {groups.map((group, g) => (
            <div
              key={group.map((i) => slides[i]!.key).join('|')}
              ref={(el) => {
                pageRefs.current[g] = el
              }}
              role="group"
              aria-roledescription="slide"
              aria-label={t('homewall.slidePage', { n: g + 1, total: pages })}
              aria-hidden={g !== page}
              inert={g !== page}
              className="grid min-w-0 shrink-0 basis-full gap-3 px-1.5"
              style={{ gridTemplateColumns: group.map((i) => `minmax(0,${slides[i]!.weight}fr)`).join(' ') }}
            >
              {group.map((i) => (
                <div key={slides[i]!.key} className="flex min-w-0">
                  {slides[i]!.node}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {pages > 1 && (
        <div className="mt-2 flex items-center justify-center gap-1">
          <button
            type="button"
            onClick={() => setPaused((p) => !p)}
            aria-label={paused ? t('homewall.slidePlay') : t('homewall.slidePause')}
            className={controlClass}
          >
            {paused ? <Play className="h-4 w-4" aria-hidden /> : <Pause className="h-4 w-4" aria-hidden />}
          </button>
          <button
            type="button"
            onClick={() => setIndex(prevIndex(page, pages))}
            aria-label={t('homewall.slidePrev')}
            className={controlClass}
          >
            <ChevronLeft className="h-4 w-4" aria-hidden />
          </button>
          {Array.from({ length: pages }, (_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setIndex(i)}
              aria-label={t('homewall.slidePage', { n: i + 1, total: pages })}
              aria-current={i === page ? 'true' : undefined}
              className="flex h-6 w-6 items-center justify-center pointer-coarse:h-11 pointer-coarse:w-11"
            >
              <span
                className={`block h-2 rounded-full motion-safe:transition-[width] ${
                  i === page ? 'w-5 bg-violet-600 dark:bg-violet-400' : 'w-2 bg-slate-400 dark:bg-slate-500'
                }`}
              />
            </button>
          ))}
          <button
            type="button"
            onClick={() => setIndex(nextIndex(page, pages))}
            aria-label={t('homewall.slideNext')}
            className={controlClass}
          >
            <ChevronRight className="h-4 w-4" aria-hidden />
          </button>
        </div>
      )}
    </section>
  )
}
